import { useRef, useState } from 'react';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { Dialog } from '@/ui/Dialog';
import { Field, Input, Select, Textarea } from '@/ui/Field';
import { cn } from '@/lib/cn';
import {
  ETIQUETA_DE_GRUPO,
  ROLES,
  useAreas,
  useBuscarDestinatarios,
  useEnviar,
  useSubirAdjunto,
  type AdjuntoSubido,
  type GrupoDeDestinatarios,
} from './api';

/**
 * Compositor.tsx — opción 3 del módulo 6: escribir un mensaje directo.
 *
 * Aquí estaban los tres defectos graves del módulo, todos medidos en el
 * navegador el 2026-09-15.
 *
 * ## 1 · Los cuatro selectores de destinatario no existían para el teclado
 *
 *     <span class="recipient-chip active" data-group="todos_activos"
 *           onclick="PageNotifications.selectGroup(this)">Todos los activos</span>
 *
 * Cuatro `<span onclick>` con `tabIndex −1`, que ni siquiera retenían el foco
 * al llamarles `.focus()`. Como el grupo de fábrica es `todos_activos` y no
 * había otra forma de cambiarlo, **quien navegaba con teclado solo podía
 * enviar a toda la empresa**: no podía acotar a un área, a un rol ni a
 * personas concretas. Es WCAG 2.1.1, nivel A.
 *
 * Y el CSS de esos mismos chips afirmaba lo contrario: «Son <button> desde la
 * revisión de Formación 2026-08-28 (antes <span>, sin acceso por teclado)».
 * Formación arregló su mitad; esta se quedó atrás.
 *
 * Ahora son `<button role="radio">` dentro de un `radiogroup`, el mismo patrón
 * que `page-course-detail.js:428` y que los selectores de vista del Directorio.
 *
 * ## 2 · El nombre de la persona viajaba dentro de un atributo `onchange`
 *
 *     onchange="…toggleRecipient(${u.id}, '${(u.full_name||'').replace(/'/g,"")}', …)"
 *
 * Reproducido con la plantilla real:
 *
 *     María D'Angelo        →  «María DAngelo»  (apóstrofo borrado)
 *     Ana "La Jefa" Ruiz    →  toggleRecipient NO se llega a llamar
 *
 * Una comilla doble cierra el atributo y el resto del nombre se convierte en
 * atributos HTML. La casilla se marca, **y esa persona no entra en la lista de
 * destinatarios**. Sin ningún error. Con una carga bien formada el manejador
 * inyectado llega a ejecutarse.
 *
 * Aquí el nombre no se serializa a ninguna parte: viaja como dato en el estado.
 *
 * ## 3 · El chip pintaba el nombre crudo, y su gemela sí escapaba
 *
 * `_searchRecipients` pasaba `full_name` por `Utils.escapeHtml`; dos funciones
 * más abajo, `toggleRecipient` lo metía en `innerHTML` tal cual. Comprobado:
 * un `<img onerror>` en el nombre **se ejecutaba**. React escapa por
 * construcción.
 *
 * ## 4 · La vista previa enseñaba la clave de la columna
 *
 *     Destinatarios: todos_activos · Canales: Bandeja interna, Correo
 *
 * Es la última pantalla antes de escribir a toda la empresa. Ahora dice
 * «Todos los colaboradores activos» y cuántos adjuntos van.
 */

const GRUPOS: Array<{ clave: GrupoDeDestinatarios; etiqueta: string }> = [
  { clave: 'todos_activos', etiqueta: 'Todos los activos' },
  { clave: 'por_area', etiqueta: 'Por área' },
  { clave: 'por_rol', etiqueta: 'Por rol' },
  { clave: 'seleccion_manual', etiqueta: 'Selección manual' },
];

export function Compositor({ alEnviar }: { alEnviar: () => void }) {
  const [titulo, setTitulo] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [esHtml, setEsHtml] = useState(false);
  const [grupo, setGrupo] = useState<GrupoDeDestinatarios>('todos_activos');
  const [areaId, setAreaId] = useState('');
  const [rol, setRol] = useState<string>(ROLES[0].clave);
  const [elegidos, setElegidos] = useState<Map<number, string>>(new Map());
  const [busqueda, setBusqueda] = useState('');
  const [archivos, setArchivos] = useState<File[]>([]);
  const [porCorreo, setPorCorreo] = useState(true);
  const [programar, setProgramar] = useState('');
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [previa, setPrevia] = useState(false);
  const [subiendo, setSubiendo] = useState(false);

  const refTitulo = useRef<HTMLInputElement>(null);
  const refMensaje = useRef<HTMLTextAreaElement>(null);
  const refArchivos = useRef<HTMLInputElement>(null);
  const refsGrupo = useRef<Array<HTMLButtonElement | null>>([]);

  const esManual = grupo === 'seleccion_manual';
  const { data: areas, isLoading: cargandoAreas } = useAreas(grupo === 'por_area');
  const { data: encontrados, isFetching: buscando } = useBuscarDestinatarios(busqueda, esManual);
  const subirAdjunto = useSubirAdjunto();
  const enviar = useEnviar();

  /** Flechas dentro del grupo de radio, como manda el patrón WAI-ARIA. */
  function tecladoEnGrupo(e: React.KeyboardEvent, i: number) {
    const siguiente =
      e.key === 'ArrowRight' || e.key === 'ArrowDown' ? i + 1
        : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? i - 1
          : e.key === 'Home' ? 0
            : e.key === 'End' ? GRUPOS.length - 1
              : null;
    if (siguiente == null) return;
    e.preventDefault();
    const destino = (siguiente + GRUPOS.length) % GRUPOS.length;
    const elegido = GRUPOS[destino];
    if (!elegido) return;
    setGrupo(elegido.clave);
    refsGrupo.current[destino]?.focus();
  }

  function validar(): boolean {
    const nuevos: Record<string, string> = {};
    if (!titulo.trim()) nuevos.titulo = 'El mensaje necesita un asunto.';
    if (!mensaje.trim()) nuevos.mensaje = 'Escribe el cuerpo del mensaje.';
    if (esManual && elegidos.size === 0) nuevos.grupo = 'Busca y marca al menos una persona.';
    if (grupo === 'por_area' && !areaId) nuevos.grupo = 'Selecciona un área.';
    setErrores(nuevos);
    if (nuevos.titulo) refTitulo.current?.focus();
    else if (nuevos.mensaje) refMensaje.current?.focus();
    return Object.keys(nuevos).length === 0;
  }

  async function enviarAhora() {
    if (!validar()) return;

    // Los adjuntos se suben primero, uno a uno, como hacía el vanilla. Si uno
    // falla no se manda nada: es preferible a una notificación a media empresa
    // con un adjunto de menos y sin que nadie se entere.
    let subidos: AdjuntoSubido[] = [];
    if (archivos.length) {
      setSubiendo(true);
      try {
        for (const f of archivos) subidos.push(await subirAdjunto.mutateAsync(f));
      } catch (e) {
        setSubiendo(false);
        Utils.toast(
          'No se pudo subir un adjunto: ' + (e instanceof Error ? e.message : ''), 'error',
        );
        return;
      }
      setSubiendo(false);
    }

    enviar.mutate(
      {
        title: titulo.trim(),
        message: mensaje.trim(),
        recipient_group: grupo,
        recipient_area_id: grupo === 'por_area' ? Number(areaId) || null : null,
        recipient_role: grupo === 'por_rol' ? rol : null,
        user_ids: esManual ? [...elegidos.keys()] : null,
        channel_email: porCorreo,
        channel_push: false,
        channel_feed: false,
        body_is_html: esHtml,
        attachments: subidos,
        schedule_at: programar ? new Date(programar).toISOString() : null,
      },
      {
        onSuccess: (r) => {
          Utils.toast(`Notificación enviada a ${r.recipient_count} destinatarios`, 'success');
          setTitulo(''); setMensaje(''); setArchivos([]);
          setProgramar('');
          if (refArchivos.current) refArchivos.current.value = '';
          alEnviar();
        },
        onError: (e: unknown) =>
          Utils.toast(e instanceof Error ? e.message : 'No se pudo enviar', 'error'),
      },
    );
  }

  const ocupado = enviar.isPending || subiendo;

  return (
    <Card className="max-w-3xl p-5">
      {/* El aviso de alcance. El enlace es un <a> con el alias del HASH_ALIAS,
          no un navigate() con una ruta inventada — que es justo el defecto que
          tenía el compositor de Novedades. */}
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-sm border border-line
                      bg-info/8 px-3 py-2 text-2 text-ink-2">
        <i className="bi bi-info-circle text-info-ink" aria-hidden="true" />
        <span className="min-w-45 flex-1">
          Este envío llega a la <strong>bandeja interna</strong> del colaborador
          {porCorreo ? ' y a su correo' : ''}. ¿Necesitas publicar en el muro o
          segmentar por empresa, sede, cargo o etiqueta?
        </span>
        <a
          href="#centro-notificaciones"
          className="inline-flex min-h-9 pointer-coarse:min-h-11 items-center gap-1.5 rounded-sm border-2 border-brand-ink
                     px-3 text-2 font-semibold text-brand-ink hover:bg-brand hover:text-on-brand
                     focus-visible:focus-anillo"
        >
          <i className="bi bi-megaphone" aria-hidden="true" /> Ir a Comunicaciones
        </a>
      </div>

      <div className="flex flex-col gap-4">
        <Field label="Asunto" error={errores.titulo}>
          <Input
            ref={refTitulo}
            value={titulo}
            onChange={(e) => { setTitulo(e.target.value); setErrores((x) => ({ ...x, titulo: '' })); }}
            placeholder="Asunto del mensaje"
          />
        </Field>

        <fieldset className="m-0 border-0 p-0">
          <legend className="mb-1.5 p-0 text-3 font-semibold text-ink">Formato del mensaje</legend>
          <div className="flex flex-wrap gap-x-5">
            {[
              { v: false, t: 'Texto normal' },
              { v: true, t: 'HTML' },
            ].map(({ v, t }) => (
              <label key={t} className="flex min-h-11 items-center gap-2 text-3">
                <input
                  type="radio"
                  name="formato"
                  checked={esHtml === v}
                  onChange={() => setEsHtml(v)}
                  className="size-4"
                />
                {t}
              </label>
            ))}
          </div>
        </fieldset>

        <Field
          label="Mensaje"
          error={errores.mensaje}
          ayuda={esHtml ? 'Se envía tal cual: usa solo HTML de confianza.' : undefined}
        >
          <Textarea
            ref={refMensaje}
            rows={6}
            value={mensaje}
            onChange={(e) => { setMensaje(e.target.value); setErrores((x) => ({ ...x, mensaje: '' })); }}
            placeholder={esHtml ? '<p>Hola…</p>' : 'Cuerpo del mensaje…'}
          />
        </Field>

        {/* `id` explícito en los dos lados: un `<input>` crudo no lee el id del
            contexto de `Field` —eso solo lo hacen `Input`, `Select` y
            `Textarea`—, así que sin esto la etiqueta «Adjuntos» no quedaba
            asociada: pulsarla no abría el selector y el lector de pantalla leía
            el campo sin nombre. Encontrado el 2026-09-16 al repetir el patrón
            en la Biblioteca. */}
        <Field label="Adjuntos" opcional id="notif-adjuntos">
          <input
            id="notif-adjuntos"
            ref={refArchivos}
            type="file"
            multiple
            accept=".pdf,.xlsx,.xls,.csv,.txt,.doc,.docx,image/*,application/pdf"
            onChange={(e) => setArchivos([...(e.target.files ?? [])])}
            className="block w-full text-2 file:mr-3 file:min-h-9 pointer-coarse:file:min-h-11 file:rounded-sm file:border-0
                       file:bg-sunken file:px-3 file:text-2 file:font-semibold file:text-ink"
          />
        </Field>
        {archivos.length > 0 && (
          <ul className="-mt-2 flex flex-wrap gap-1.5">
            {archivos.map((f) => (
              <li
                key={f.name}
                className="inline-flex items-center gap-1.5 rounded-xs bg-sunken px-2 py-0.5 text-1"
              >
                <i className="bi bi-paperclip" aria-hidden="true" />
                {f.name}
              </li>
            ))}
          </ul>
        )}

        {/* ── Destinatarios ───────────────────────────────────────────── */}
        <div>
          <p id="etiqueta-grupo" className="mb-1.5 text-3 font-semibold text-ink">
            Destinatarios
          </p>
          <div role="radiogroup" aria-labelledby="etiqueta-grupo" className="flex flex-wrap gap-2">
            {GRUPOS.map((g, i) => {
              const activo = grupo === g.clave;
              return (
                <button
                  key={g.clave}
                  ref={(el) => { refsGrupo.current[i] = el; }}
                  type="button"
                  role="radio"
                  aria-checked={activo}
                  // Un solo punto de parada para todo el grupo: se entra con
                  // Tab y se recorre con flechas. Tabular por cada opción
                  // obliga a pasar por las cuatro para salir del formulario.
                  tabIndex={activo ? 0 : -1}
                  onClick={() => { setGrupo(g.clave); setErrores((x) => ({ ...x, grupo: '' })); }}
                  onKeyDown={(e) => tecladoEnGrupo(e, i)}
                  className={cn(
                    'min-h-11 rounded-xl border-2 px-3.5 text-2 font-semibold transition-colors',
                    'focus-visible:focus-anillo',
                    activo
                      ? 'border-brand-ink bg-brand text-on-brand'
                      : 'border-line text-ink-2 hover:border-brand-ink hover:text-brand-ink',
                  )}
                >
                  {g.etiqueta}
                </button>
              );
            })}
          </div>

          {errores.grupo && (
            <p role="alert" className="mt-1.5 text-2 font-medium text-danger-ink">
              {errores.grupo}
            </p>
          )}

          {grupo === 'por_area' && (
            <div className="mt-3 max-w-xs">
              <Field label="Área">
                <Select value={areaId} onChange={(e) => { setAreaId(e.target.value); setErrores((x) => ({ ...x, grupo: '' })); }}>
                  <option value="">{cargandoAreas ? 'Cargando áreas…' : '— Selecciona un área —'}</option>
                  {(areas ?? []).map((a) => (
                    <option key={a.id} value={String(a.id)}>{a.name}</option>
                  ))}
                </Select>
              </Field>
            </div>
          )}

          {grupo === 'por_rol' && (
            <div className="mt-3 max-w-xs">
              <Field label="Rol">
                <Select value={rol} onChange={(e) => setRol(e.target.value)}>
                  {ROLES.map((r) => (
                    <option key={r.clave} value={r.clave}>{r.etiqueta}</option>
                  ))}
                </Select>
              </Field>
            </div>
          )}

          {esManual && (
            <div className="mt-3 flex flex-col gap-2">
              <Field label="Buscar colaborador">
                <Input
                  type="search"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Nombre de la persona…"
                  className="max-w-sm"
                />
              </Field>
              <div className="max-h-48 max-w-sm overflow-auto rounded-sm border border-line">
                {buscando && !encontrados ? (
                  <p className="p-3 text-2 text-muted">Buscando…</p>
                ) : !encontrados?.length ? (
                  <p className="p-3 text-2 text-muted">Sin resultados.</p>
                ) : (
                  <ul>
                    {encontrados.map((p) => (
                      <li key={p.id}>
                        <label className="flex min-h-11 cursor-pointer items-center gap-2 px-3 text-3 hover:bg-sunken">
                          <input
                            type="checkbox"
                            className="size-4"
                            checked={elegidos.has(p.id)}
                            onChange={() => {
                              setElegidos((prev) => {
                                const copia = new Map(prev);
                                if (copia.has(p.id)) copia.delete(p.id);
                                else copia.set(p.id, p.full_name);
                                return copia;
                              });
                              setErrores((x) => ({ ...x, grupo: '' }));
                            }}
                          />
                          <span className="min-w-0 flex-1 truncate">
                            {p.full_name}
                            {p.area && <span className="ml-1.5 text-muted">· {p.area}</span>}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {elegidos.size > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-2 text-muted">
                    {elegidos.size} seleccionada{elegidos.size === 1 ? '' : 's'}:
                  </span>
                  {[...elegidos].map(([id, nombre]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() =>
                        setElegidos((prev) => { const c = new Map(prev); c.delete(id); return c; })
                      }
                      className="inline-flex min-h-8 items-center gap-1.5 rounded-xs bg-brand px-2
                                 text-1 font-semibold text-on-brand hover:bg-brand-strong
                                 focus-visible:focus-anillo"
                    >
                      {nombre}
                      <i className="bi bi-x-lg" aria-hidden="true" />
                      <span className="sr-only">Quitar de la lista</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Entrega ─────────────────────────────────────────────────── */}
        <fieldset className="m-0 border-0 p-0">
          <legend className="mb-1.5 p-0 text-3 font-semibold text-ink">Entrega</legend>
          <div className="flex flex-wrap items-center gap-x-5">
            <span className="inline-flex items-center gap-1.5 rounded-xs bg-sunken px-2 py-0.5 text-2 text-ink-2">
              <i className="bi bi-bell" aria-hidden="true" /> Bandeja interna (siempre)
            </span>
            <label className="flex min-h-11 items-center gap-2 text-3">
              <input
                type="checkbox"
                className="size-4"
                checked={porCorreo}
                onChange={(e) => setPorCorreo(e.target.checked)}
              />
              Enviar también por correo
            </label>
          </div>
        </fieldset>

        <Field label="Programar envío" opcional ayuda="Si lo dejas vacío, sale al confirmar.">
          <Input
            type="datetime-local"
            value={programar}
            onChange={(e) => setProgramar(e.target.value)}
            className="max-w-xs"
          />
        </Field>

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={() => { if (validar()) setPrevia(true); }}>
            <i className="bi bi-eye" aria-hidden="true" /> Vista previa
          </Button>
          <Button onClick={enviarAhora} loading={ocupado}>
            <i className="bi bi-send" aria-hidden="true" />
            {programar ? 'Programar envío' : 'Enviar ahora'}
          </Button>
        </div>
      </div>

      <VistaPrevia
        abierta={previa}
        onCerrar={() => setPrevia(false)}
        titulo={titulo}
        mensaje={mensaje}
        esHtml={esHtml}
        grupo={grupo}
        cuantosAdjuntos={archivos.length}
        porCorreo={porCorreo}
      />
    </Card>
  );
}

/**
 * La vista previa del mensaje.
 *
 * El HTML se pinta dentro de un `<iframe sandbox srcdoc>` y no con
 * `dangerouslySetInnerHTML`. Es lo único que enseña el mensaje **tal como se
 * va a enviar** sin traerlo al DOM del portal: sin `allow-scripts` no corre
 * nada, y lo que se vea es exactamente lo que recibirá quien lo abra.
 *
 * Sanearlo con `Utils.sanitizeRich` habría sido la otra opción, y es peor
 * aquí: el correo NO pasa por ese saneador, así que la vista previa enseñaría
 * algo distinto de lo que sale — y una previsualización que miente es peor que
 * no tenerla.
 */
function VistaPrevia({
  abierta, onCerrar, titulo, mensaje, esHtml, grupo, cuantosAdjuntos, porCorreo,
}: {
  abierta: boolean; onCerrar: () => void;
  titulo: string; mensaje: string; esHtml: boolean;
  grupo: GrupoDeDestinatarios; cuantosAdjuntos: number; porCorreo: boolean;
}) {
  const canales = ['Bandeja interna', porCorreo ? 'Correo' : null].filter(Boolean).join(', ');
  return (
    <Dialog
      abierto={abierta}
      onCambio={(v) => !v && onCerrar()}
      titulo="Vista previa del mensaje"
      descripcion="Así lo va a recibir el colaborador."
      ancho="max-w-2xl"
      pie={<Button onClick={onCerrar}>Cerrar</Button>}
    >
      <dl className="mb-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-2">
        <dt className="font-semibold text-ink">Destinatarios</dt>
        <dd className="text-muted">{ETIQUETA_DE_GRUPO[grupo]}</dd>
        <dt className="font-semibold text-ink">Canales</dt>
        <dd className="text-muted">{canales}</dd>
        <dt className="font-semibold text-ink">Formato</dt>
        <dd className="text-muted">{esHtml ? 'HTML' : 'Texto'}</dd>
        {cuantosAdjuntos > 0 && (
          <>
            <dt className="font-semibold text-ink">Adjuntos</dt>
            <dd className="text-muted">{cuantosAdjuntos}</dd>
          </>
        )}
      </dl>

      <div className="overflow-hidden rounded-sm border border-line">
        <p className="bg-brand px-4 py-2.5 font-semibold text-on-brand">{titulo}</p>
        {esHtml ? (
          <iframe
            // `key` con el contenido: cambiar `srcdoc` en un iframe YA CARGADO
            // no lo recarga, y React reutiliza el mismo nodo entre renders. Sin
            // esto la vista previa enseña el mensaje ANTERIOR — comprobado en el
            // navegador el 2026-09-15: el atributo `srcdoc` era el correcto y lo
            // pintado seguía siendo lo de antes. Una previsualización que miente
            // es peor que no tenerla.
            key={mensaje}
            title="Cuerpo del mensaje"
            sandbox=""
            srcDoc={mensaje}
            className="block h-64 w-full border-0 bg-white"
          />
        ) : (
          // `whitespace-pre-wrap` hace lo que el vanilla hacía reemplazando
          // cada \n por un <br>, sin construir marcado con el texto.
          <p className="max-h-64 overflow-auto whitespace-pre-wrap p-4 text-3">{mensaje}</p>
        )}
      </div>
    </Dialog>
  );
}
